// Sources: libms ui/Toast.tsx (structure, icons, manual dismiss, per-kind TTL) + exams ui/toast.tsx (info tone); decisions: module-signal singleton (one bus per bundle) rendered by a single <Toaster/>, tones via daisyUI's SOLID alert-<role> classes.
//
// THE SURFACE — Spectrum's toast, adopted 2026-09-19 (owner decision D3, audit
// COL-09; DEV-GUIDE-002 §12.4 "Toast — SOLID 900 fill + white (light) / black
// (darkest) content, shadow-raised; informative / positive / negative only;
// neutral = gray-800; there is no notice toast"). Adobe's comp-toast paints
// `toast-<variant>-background-color` = the role's background-color-default —
// the 900 fill — with `toast-text-and-icon-color` white, and the toast page's
// semantic variants are neutral / informative / positive / negative, nothing
// else. Before this the Toaster was a soft two-tone alert with a decorative
// gray-200 border and no elevation — the one floating surface in the kit
// without the drop shadow that exists for exactly this family.
//
//   fill      daisyUI's solid `alert-<role>`: --alert-color = --color-<role>,
//             the 900 stop per theme (blue-900 / green-900 / red-900 —
//             base.css). `neutral` has no daisyUI alert class, so its two
//             variables are set inline to --color-neutral, gray-800 in both
//             themes (#222222 L / #ebebeb D).
//   content   --color-<role>-content — white on the light 900s (5.39–5.41),
//             BLACK on the darkest ones (8.57–8.60): §12.4's measured pair,
//             and the ink Adobe's own darkest toast carries. The icon inherits
//             it; the dismiss X takes IconButton's `on-accent` variant, whose
//             primary-content ink is the same white / black pair every solid
//             role fill in base.css mints.
//   shadow    `shadow-raised` — 0 1px 4px black at .15 L / .8 D
//             (--spectrum-drop-shadow), the ONE shadow, for transient
//             surfaces only (§12.1). No border: daisyUI's alert border is
//             painted in the fill's own colour and reads as none.
//   corner    --radius-500 (8px) from base.css's `.alert` rule — Adobe's
//             inlinealert and toast share corner-radius-100.
//
// KINDS: `success` (positive), `error` (negative), `info` (informative) keep
// the names the ~490 call sites pass; `neutral` is new for a message with no
// status at all ("Sync scheduled for tonight"). There has never been a notice
// / warning kind on this bus — the demo's "a warning is an error you have not
// decided about yet" — and D3 keeps it that way: a caller with a warning
// picks `error` when the person must act, else `info`.
import { For, createSignal } from 'solid-js';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-solid';
import { IconButton } from '../icon-button/index.js';

/** Spectrum's four semantic toasts: positive, negative, informative, neutral (no notice). */
export type ToastKind = 'success' | 'error' | 'info' | 'neutral';

interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
}

const [items, setItems] = createSignal<ToastItem[]>([]);
let seq = 0;

function push(kind: ToastKind, text: string, ttl: number) {
  const id = ++seq;
  setItems((prev) => [...prev, { id, kind, text }]);
  setTimeout(() => dismiss(id), ttl);
}

function dismiss(id: number) {
  setItems((prev) => prev.filter((t) => t.id !== id));
}

export const toast = {
  success: (text: string) => push('success', text, 3500),
  error: (text: string) => push('error', text, 5000),
  info: (text: string) => push('info', text, 4000),
  /** A message with no status — no icon, the gray-800 fill. */
  neutral: (text: string) => push('neutral', text, 4000),
};

/**
 * One complete static string per kind — the solid fill + its content ink.
 * Neutral sets daisyUI's two alert variables itself (there is no
 * alert-neutral) so the fill AND the invisible border both take gray-800.
 */
const TONE: Record<ToastKind, string> = {
  success: 'alert-success',
  error: 'alert-error',
  info: 'alert-info',
  neutral: '[--alert-color:var(--color-neutral)] [--alert-border-color:var(--color-neutral)] text-neutral-content',
};

/** The status glyph per kind; neutral carries none (Spectrum's neutral toast is text only). */
const ICON: Partial<Record<ToastKind, typeof CheckCircle2>> = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
};

export function Toaster() {
  return (
    // The live region lives on the persistent container — a region that pre-exists gets its inserted items announced.
    <div class="toast toast-bottom toast-end z-[100] pointer-events-none" role="status" aria-live="polite">
      <For each={items()}>
        {(t) => {
          const Icon = ICON[t.kind];
          return (
            <div
              data-kind={t.kind}
              class={`alert ${TONE[t.kind]} shadow-raised pointer-events-auto flex items-center gap-2.5 pl-3 pr-2 py-2.5 min-w-[260px] max-w-sm`}
            >
              {Icon ? <Icon size={16} class="shrink-0" aria-hidden="true" /> : null}
              <span class="text-xs flex-1 leading-snug text-start">{t.text}</span>
              {/* @erp/ui/icon-button — the 32px hit floor (40 at touch): a
                  toast is transient, so its dismiss must not also be the
                  hardest thing on screen to hit. `s`: the 16px glyph beside a
                  16px status icon and text-xs. `on-accent`: the content ink
                  on a solid fill (see the header). No tooltip on a dismiss X. */}
              <IconButton icon={X} label="Dismiss" variant="on-accent" tooltip={false} onClick={() => dismiss(t.id)} />
            </div>
          );
        }}
      </For>
    </div>
  );
}
